import { motion } from "framer-motion";
import { Wheat } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "L'Impasto",
    text: "Un blend di farine siciliane — tipo 0, tipo 1, Russello, Timilia e Perciasacchi — acqua, olio evo e una lievitazione lenta di 24/48 ore a temperatura controllata. E per accontentare proprio tutti, l'impasto si fa anche integrale, ai cereali o senza glutine.",
    tags: ["Integrale", "Ai Cereali", "Senza Glutine"],
    img: "/assets/pizza-twister.png",
    alt: "Pizza Pistacchio Twister di Grano",
  },
  {
    n: "02",
    title: "Gli Ingredienti",
    text: "Burrata cremosa, mortadella, crema di pistacchio, tartufo, porchetta d'Ariccia, fiori di zucca in pastella. Ogni condimento è scelto per raccontare un territorio e una stagione.",
    img: "/assets/pizza-rigamonti.png",
    alt: "Pizza con bresaola, burrata e rucola di Grano",
  },
  {
    n: "03",
    title: "Il Territorio",
    text: "Nel cuore di Fiumefreddo di Sicilia, tra l'Etna e Taormina. Qui la formula è libera: ordini al banco e scegli il tuo tavolo all'aperto — nessun servizio in sala, solo la pizza appena sfornata e la sera siciliana. E se preferisci casa tua, asporto e domicilio in tutta la zona.",
    img: "/assets/sala.jpg",
    alt: "I tavoli all'aperto di Grano",
    framed: true,
  },
];

export default function Manifesto() {
  return (
    <section id="storia" data-testid="manifesto-section" className="relative py-28 sm:py-40 bg-ink overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-[11px] tracking-[0.45em] uppercase text-terra"
        >
          Il Manifesto
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.1, ease: EASE }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream mt-4 max-w-3xl"
        >
          Tre regole, <span className="italic text-gold">nessuna scorciatoia.</span>
        </motion.h2>

        <div className="mt-24 sm:mt-32 flex flex-col gap-32 sm:gap-44">
          {CHAPTERS.map((c, i) => (
            <div
              key={c.n}
              data-testid={`manifesto-chapter-${c.n}`}
              className={`relative flex flex-col gap-10 md:items-center ${
                i % 2 === 1 ? "md:flex-row-reverse" : "md:flex-row"
              }`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none select-none absolute -top-16 sm:-top-24 font-display text-[7rem] sm:text-[11rem] leading-none text-outline ${
                  i % 2 === 1 ? "right-0" : "left-0"
                }`}
              >
                {c.n}
              </span>

              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: EASE }}
                className="md:w-1/2 relative"
              >
                {c.framed ? (
                  <div className="relative overflow-hidden border border-white/10">
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent z-10" />
                    <img src={c.img} alt={c.alt} className="w-full h-[320px] sm:h-[420px] object-cover" />
                  </div>
                ) : (
                  <div className="relative flex justify-center py-8">
                    <div className="spotlight absolute inset-0" />
                    <img
                      src={c.img}
                      alt={c.alt}
                      className="relative w-2/3 max-w-[340px] drop-shadow-[0_30px_50px_rgba(0,0,0,0.65)] hover:scale-[1.04] transition-transform duration-700 ease-out"
                    />
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, delay: 0.15, ease: EASE }}
                className={`md:w-1/2 ${i % 2 === 1 ? "md:pr-16" : "md:pl-16"}`}
              >
                <span className="text-[11px] tracking-[0.4em] uppercase text-gold">Capitolo {c.n}</span>
                <h3 className="font-display text-3xl sm:text-4xl text-cream mt-3">{c.title}</h3>
                <p className="text-cream/60 leading-relaxed mt-5 max-w-md text-sm sm:text-base">{c.text}</p>
                {c.tags && (
                  <div data-testid="dough-tags" className="flex flex-wrap gap-3 mt-7">
                    {c.tags.map((t) => (
                      <span
                        key={t}
                        data-testid={`dough-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center gap-2 border border-gold/30 text-gold text-[10px] tracking-[0.25em] uppercase px-4 py-2 hover:bg-gold/10 transition-colors duration-300"
                      >
                        <Wheat size={12} strokeWidth={1.5} />
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
